import { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { EVENTS, formatTime, parseTimeToSeconds } from '../../lib/vdot';
import {
  isRelayEvent,
  getRelayLegOptions,
  defaultRelayLeg,
  relaySplitEvent,
  resultInputLabel,
  getRelayLegFor,
  getEventDate,
  benchmarkEventFor,
} from '../../lib/relays';
import { SEASON_OPTIONS } from '../../lib/seasons';

const PRIORITIES = [
  { value: 'A', label: 'A Race', desc: 'Peak performance goal' },
  { value: 'B', label: 'B Race', desc: 'Strong effort, no taper' },
  { value: 'C', label: 'C Race', desc: 'Training race / fitness check' },
];

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const race = new Date(dateStr + 'T00:00:00');
  return Math.round((race - today) / (1000 * 60 * 60 * 24));
}

function formatRaceDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Format a single-day race date or a multi-day range. Collapses same-
 * month ranges to "Apr 12–13, 2026" and expands cross-month ranges to
 * "Apr 30 – May 2, 2026".
 */
function formatRaceDateRange(startStr, endStr) {
  if (!startStr) return '';
  if (!endStr || endStr === startStr) return formatRaceDate(startStr);
  const start = new Date(startStr + 'T00:00:00');
  const end = new Date(endStr + 'T00:00:00');
  const sameMonth =
    start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
  if (sameMonth) {
    const mon = start.toLocaleDateString('en-US', { month: 'short' });
    return `${mon} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`;
  }
  const s = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const e = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${s} – ${e}`;
}

function parseRaceEvents(eventField) {
  if (!eventField) return [];
  try {
    const arr = JSON.parse(eventField);
    if (Array.isArray(arr)) return arr;
  } catch {}
  return [eventField];
}

function getEventLabels(eventField) {
  const evts = parseRaceEvents(eventField);
  if (!evts.length) return '';
  return evts.map(e => EVENTS[e]?.label ?? e).join(', ');
}

/**
 * Pretty-print a race's events with each relay annotated by the
 * athlete's leg. Multi-relay meets read as
 * "4×100m Relay (100m leg) · 4×400m Relay (400m leg) · 800m".
 * Falls back to the legacy single-leg behavior for older rows.
 */
function getRaceEventLabelsWithLeg(race) {
  const events = parseRaceEvents(race?.event);
  if (!events.length) return '';
  return events
    .map(e => {
      const label = EVENTS[e]?.label ?? e;
      if (!isRelayEvent(e)) return label;
      const leg = getRelayLegFor(race, e);
      return leg ? `${label} (${leg}m leg)` : label;
    })
    .join(' · ');
}

export function RaceCalendar() {
  const { profile } = useAuth();
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [editRaceId, setEditRaceId] = useState(null);
  const [raceName, setRaceName] = useState('');
  const [raceDate, setRaceDate] = useState('');
  // Optional end date for multi-day meets. Empty string = single-day.
  const [raceDateEnd, setRaceDateEnd] = useState('');
  const [raceEvents, setRaceEvents] = useState([]);
  // Per-relay leg distances (meters as string). Multi-relay support:
  // an athlete running both 4×100m and 4×400m at one meet stores
  // `{ '4x100m-relay': '100', '4x400m-relay': '400' }`.
  const [relayLegs, setRelayLegs] = useState({});
  // Per-event dates for multi-day meets. `{ eventKey: 'YYYY-MM-DD' }`
  const [eventDates, setEventDates] = useState({});
  // Explicit season tag — 'xc' / 'indoor' / 'outdoor' / '' (auto).
  const [raceSeason, setRaceSeason] = useState('');
  const [raceLocation, setRaceLocation] = useState('');
  const [racePriority, setRacePriority] = useState('B');
  const [raceNotes, setRaceNotes] = useState('');

  // Result logging state
  const [resultInputs, setResultInputs] = useState({});
  const [resultSaving, setResultSaving] = useState(null);
  const [resultErrors, setResultErrors] = useState({});
  const [savedResults, setSavedResults] = useState({});
  const [raceBenchmarks, setRaceBenchmarks] = useState([]);

  useEffect(() => {
    if (profile) fetchRaces();
  }, [profile]);

  // Pre-populate savedResults from benchmarks. Lookup keys use the
  // saved benchmark event (split for relays) and the per-event date
  // (multi-day meets), so previously-saved relay splits and Day-2
  // events both prefill correctly.
  useEffect(() => {
    if (!races.length || !raceBenchmarks.length) return;
    const prefill = {};
    races.forEach(race => {
      const events = parseRaceEvents(race.event);
      if (events.length < 2) return;
      events.forEach(ek => {
        const bmEvent = benchmarkEventFor(race, ek);
        const bmDate = getEventDate(race, ek);
        const bm =
          raceBenchmarks.find(b => b.event === bmEvent && b.race_date === bmDate && b.notes === race.name) ||
          raceBenchmarks.find(b => b.event === bmEvent && b.race_date === bmDate);
        if (bm) prefill[`${race.id}-${ek}`] = formatTime(bm.time_seconds);
      });
    });
    if (Object.keys(prefill).length) {
      setSavedResults(prev => ({ ...prefill, ...prev }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [races, raceBenchmarks]);

  async function fetchRaces() {
    setLoading(true);
    const [racesRes, bmRes] = await Promise.all([
      supabase.from('races').select('*').eq('athlete_id', profile.id).order('race_date', { ascending: true }),
      supabase.from('benchmarks').select('event, time_seconds, race_date, notes').eq('athlete_id', profile.id),
    ]);
    setRaces(racesRes.data ?? []);
    setRaceBenchmarks(bmRes.data ?? []);
    setLoading(false);
  }

  function resetRaceForm() {
    setEditRaceId(null);
    setRaceName(''); setRaceDate(''); setRaceDateEnd(''); setRaceEvents([]);
    setRelayLegs({}); setEventDates({}); setRaceSeason('');
    setRaceLocation(''); setRacePriority('B'); setRaceNotes('');
    setShowForm(false);
  }

  /**
   * Checkbox handler for the event grid. Multiple relays per race
   * are supported — each relay's leg is tracked independently in
   * `relayLegs`. Newly-checked relays default to their first leg;
   * unchecking removes that relay's entry from the map.
   */
  function toggleEventCheckbox(key, checked) {
    const relay = isRelayEvent(key);
    setRaceEvents(prev => {
      if (checked) return prev.includes(key) ? prev : [...prev, key];
      return prev.filter(p => p !== key);
    });
    if (relay) {
      setRelayLegs(prev => {
        const next = { ...prev };
        if (checked) {
          if (next[key] == null) next[key] = String(defaultRelayLeg(key) ?? '');
        } else {
          delete next[key];
        }
        return next;
      });
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);

    // Build relay_legs map for whichever relays are in the event
    // list. Old `relay_leg` (single text) is null'd — relay_legs is
    // the new source of truth. The legacy column stays in the schema
    // as a read-time fallback for pre-migration rows.
    const relayLegsForSave = {};
    for (const ek of raceEvents) {
      if (isRelayEvent(ek) && relayLegs[ek]) {
        relayLegsForSave[ek] = relayLegs[ek];
      }
    }
    // For multi-day meets, persist per-event dates. Drop entries
    // matching the start date — implied default.
    const isMultiDay = raceDateEnd && raceDateEnd !== raceDate;
    const eventDatesForSave = {};
    if (isMultiDay) {
      for (const ek of raceEvents) {
        const d = eventDates[ek];
        if (d && d !== raceDate) eventDatesForSave[ek] = d;
      }
    }
    const payload = {
      name: raceName,
      race_date: raceDate,
      race_date_end: isMultiDay ? raceDateEnd : null,
      event: raceEvents.length > 0 ? JSON.stringify(raceEvents) : null,
      relay_legs: Object.keys(relayLegsForSave).length ? relayLegsForSave : null,
      relay_leg: null,
      event_dates: Object.keys(eventDatesForSave).length ? eventDatesForSave : null,
      season: raceSeason || null,
      location: raceLocation || null,
      priority: racePriority,
      notes: raceNotes || null,
    };

    let err;
    if (editRaceId) {
      ({ error: err } = await supabase.from('races').update(payload).eq('id', editRaceId));
    } else {
      ({ error: err } = await supabase.from('races').insert({ athlete_id: profile.id, ...payload }));
    }

    if (err) { setError(err.message); setSaving(false); return; }
    resetRaceForm();
    await fetchRaces();
    setSaving(false);
  }

  async function handleLogResult(race, eventKey) {
    const inputKey = eventKey ? `${race.id}-${eventKey}` : race.id;
    const seconds = parseTimeToSeconds(resultInputs[inputKey] ?? '');
    if (!seconds) {
      setResultErrors(prev => ({ ...prev, [inputKey]: 'Enter a valid time (e.g. 4:32.56 or 16:45.00)' }));
      return;
    }
    setResultErrors(prev => ({ ...prev, [inputKey]: '' }));
    setResultSaving(race.id);

    const { error: raceErr } = await supabase
      .from('races')
      .update({ result_time_seconds: seconds })
      .eq('id', race.id);
    if (raceErr) {
      setResultErrors(prev => ({ ...prev, [inputKey]: raceErr.message }));
      setResultSaving(null);
      return;
    }

    setSavedResults(prev => ({ ...prev, [inputKey]: formatTime(seconds) }));
    setResultInputs(prev => ({ ...prev, [inputKey]: '' }));
    setResultSaving(null);
    await fetchRaces();
  }

  async function handleLogAllResults(race) {
    const events = parseRaceEvents(race.event);
    const unsaved = events.filter(ek => {
      const inputKey = `${race.id}-${ek}`;
      return !savedResults[inputKey] && !(events.indexOf(ek) === 0 && race.result_time_seconds);
    });

    // Validate all inputs
    const newErrors = {};
    for (const ek of unsaved) {
      const inputKey = `${race.id}-${ek}`;
      const val = resultInputs[inputKey] ?? '';
      if (val.trim() && !parseTimeToSeconds(val)) {
        newErrors[inputKey] = 'Invalid time format';
      }
    }
    if (Object.keys(newErrors).length) {
      setResultErrors(prev => ({ ...prev, ...newErrors }));
      return;
    }

    setResultSaving(race.id);
    setResultErrors({});

    const toSave = unsaved.filter(ek => {
      const inputKey = `${race.id}-${ek}`;
      return (resultInputs[inputKey] ?? '').trim() !== '';
    });

    for (let i = 0; i < toSave.length; i++) {
      const ek = toSave[i];
      const inputKey = `${race.id}-${ek}`;
      const seconds = parseTimeToSeconds(resultInputs[inputKey]);
      if (!seconds) continue;

      if (i === 0) {
        const { error: raceErr } = await supabase
          .from('races')
          .update({ result_time_seconds: seconds })
          .eq('id', race.id);
        if (raceErr) {
          setResultErrors(prev => ({ ...prev, [inputKey]: raceErr.message }));
          setResultSaving(null);
          return;
        }
      }

      // For relay events, convert to the split pseudo-event using
      // the per-event leg lookup. For multi-day meets, the
      // benchmark's race_date is the event-specific date.
      const bmEvent = benchmarkEventFor(race, ek);
      const bmDate = getEventDate(race, ek);
      // Dedupe: delete any existing (athlete, event, date, race_name)
      // row before inserting. Season copied from the race so the
      // improvement tracker groups by what the user chose.
      await supabase.from('benchmarks')
        .delete()
        .eq('athlete_id', profile.id)
        .eq('event', bmEvent)
        .eq('race_date', bmDate)
        .eq('notes', race.name);
      const { error: bmErr } = await supabase.from('benchmarks').insert({
        athlete_id: profile.id,
        event: bmEvent,
        time_seconds: seconds,
        race_date: bmDate,
        notes: race.name,
        season: race.season ?? null,
      });
      if (bmErr) {
        setResultErrors(prev => ({ ...prev, [inputKey]: bmErr.message }));
        setResultSaving(null);
        return;
      }

      setSavedResults(prev => ({ ...prev, [inputKey]: formatTime(seconds) }));
    }

    setResultInputs(prev => {
      const next = { ...prev };
      toSave.forEach(ek => delete next[`${race.id}-${ek}`]);
      return next;
    });
    setResultSaving(null);
    await fetchRaces();
  }

  function renderResultInputs(race, days = null) {
    const events = parseRaceEvents(race.event);

    if (days !== null && days > 0) {
      return <div style={{ fontSize: 11, color: 'var(--gray-400)', fontStyle: 'italic' }}>Available on race day</div>;
    }

    if (events.length === 0) {
      const inputKey = race.id;
      const alreadySaved = race.result_time_seconds || savedResults[inputKey];
      if (alreadySaved) {
        return <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{savedResults[inputKey] ?? formatTime(race.result_time_seconds)}</div>;
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input className="form-input" style={{ width: 90, padding: '5px 8px', fontSize: 12 }}
              type="text" placeholder="4:32.56"
              value={resultInputs[inputKey] ?? ''}
              onChange={e => setResultInputs(prev => ({ ...prev, [inputKey]: e.target.value }))} />
            <button className="btn-xs btn-xs-primary"
              onClick={() => handleLogResult(race, null)}
              disabled={resultSaving === race.id}>
              {resultSaving === race.id ? '...' : 'Log'}
            </button>
          </div>
          {resultErrors[inputKey] && <div style={{ fontSize: 11, color: 'var(--red, #dc2626)' }}>{resultErrors[inputKey]}</div>}
        </div>
      );
    }

    // Multi-event: all inputs + one Save button
    const allSaved = events.every((ek, i) => {
      const inputKey = `${race.id}-${ek}`;
      return savedResults[inputKey] || (i === 0 && race.result_time_seconds);
    });

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {events.map((eventKey, i) => {
          const inputKey = `${race.id}-${eventKey}`;
          const savedTime = savedResults[inputKey] ?? (i === 0 && race.result_time_seconds ? formatTime(race.result_time_seconds) : null);
          // Show the split label ("800m (split)") on relay inputs so
          // it matches what the benchmark gets saved as. Per-relay
          // leg lookup handles multi-relay meets.
          const label = resultInputLabel(eventKey, getRelayLegFor(race, eventKey), EVENTS);
          if (savedTime) {
            return (
              <div key={eventKey} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>{label}:</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{savedTime}</span>
              </div>
            );
          }
          return (
            <div key={eventKey} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--gray-400)', minWidth: 56 }}>{label}:</span>
                <input className="form-input" style={{ width: 90, padding: '5px 8px', fontSize: 12 }}
                  type="text" placeholder="4:32.56"
                  value={resultInputs[inputKey] ?? ''}
                  onChange={e => setResultInputs(prev => ({ ...prev, [inputKey]: e.target.value }))} />
              </div>
              {resultErrors[inputKey] && <div style={{ fontSize: 11, color: 'var(--red, #dc2626)', paddingLeft: 62 }}>{resultErrors[inputKey]}</div>}
            </div>
          );
        })}
        {!allSaved && (
          <button className="btn-xs btn-xs-primary" style={{ alignSelf: 'flex-start', marginTop: 2 }}
            onClick={() => handleLogAllResults(race)}
            disabled={resultSaving === race.id}>
            {resultSaving === race.id ? 'Saving...' : 'Save Results'}
          </button>
        )}
      </div>
    );
  }

  async function handleDelete(id) {
    await supabase.from('races').delete().eq('id', id);
    setRaces(prev => prev.filter(r => r.id !== id));
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Multi-day races partition by their LAST day — a 3-day meet that
  // started yesterday and ends tomorrow still belongs in Upcoming.
  const effectiveEnd = r => new Date((r.race_date_end ?? r.race_date) + 'T00:00:00');
  const upcoming = races.filter(r => effectiveEnd(r) >= today);
  const past = races.filter(r => effectiveEnd(r) < today);

  return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <p className="page-eyebrow">Athlete</p>
          <h1 className="page-title">Race Calendar</h1>
        </div>
        <button
          className="btn btn-primary"
          style={{ width: 'auto', padding: '10px 20px' }}
          onClick={() => { if (showForm) resetRaceForm(); else setShowForm(true); }}
        >
          {showForm ? 'Cancel' : '+ Add Race'}
        </button>
      </div>

      {showForm && (
        <div className="section-card" style={{ marginBottom: 24 }}>
          <div className="section-card-header">
            <span className="section-card-title">{editRaceId ? 'Edit Race' : 'Add Race'}</span>
          </div>
          <div style={{ padding: '20px 24px' }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Race Name</label>
                  <input
                    className="form-input"
                    type="text"
                    value={raceName}
                    onChange={e => setRaceName(e.target.value)}
                    placeholder="e.g. State Championships"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input
                    className="form-input"
                    type="date"
                    value={raceDate}
                    onChange={e => setRaceDate(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">End Date (optional — for multi-day meets)</label>
                  <input
                    className="form-input"
                    type="date"
                    value={raceDateEnd}
                    min={raceDate || undefined}
                    onChange={e => setRaceDateEnd(e.target.value)}
                    style={{ maxWidth: 240 }}
                  />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Events (optional — select one or more)</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, maxHeight: 148, overflowY: 'auto', padding: 8, border: '1px solid var(--gray-200)', borderRadius: 6 }}>
                    {Object.entries(EVENTS)
                      // Hide the `*-split` pseudo-events from the race
                      // picker — those belong on benchmark rows, not
                      // race rows. The athlete picks the relay event;
                      // the split is derived from the leg selector.
                      .filter(([key]) => !key.endsWith('-split'))
                      .map(([key, ev]) => (
                      <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, cursor: 'pointer', padding: '2px 0' }}>
                        <input
                          type="checkbox"
                          checked={raceEvents.includes(key)}
                          onChange={e => toggleEventCheckbox(key, e.target.checked)}
                        />
                        {ev.label}
                      </label>
                    ))}
                  </div>
                </div>
                {raceEvents.filter(isRelayEvent).map(relayKey => {
                  const legs = getRelayLegOptions(relayKey) ?? [];
                  if (legs.length <= 1) return null;
                  return (
                    <div key={relayKey} className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label className="form-label">
                        Your Leg in {EVENTS[relayKey]?.label ?? relayKey}
                      </label>
                      <select
                        className="form-input form-select"
                        value={relayLegs[relayKey] ?? ''}
                        onChange={e => setRelayLegs(prev => ({ ...prev, [relayKey]: e.target.value }))}
                        style={{ maxWidth: 240 }}
                        required
                      >
                        <option value="">— Select leg —</option>
                        {legs.map(m => (
                          <option key={m} value={String(m)}>{m}m</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
                {raceDateEnd && raceDateEnd !== raceDate && raceEvents.length > 0 && (
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Event Days (multi-day meet)</label>
                    <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '0 0 8px' }}>
                      Pick which day each event runs. Defaults to the start date.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                      {raceEvents.map(ek => (
                        <div key={ek} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600 }}>
                            {EVENTS[ek]?.label ?? ek}
                          </span>
                          <input
                            type="date"
                            className="form-input"
                            min={raceDate || undefined}
                            max={raceDateEnd || undefined}
                            value={eventDates[ek] ?? raceDate ?? ''}
                            onChange={e => setEventDates(prev => ({ ...prev, [ek]: e.target.value }))}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select
                    className="form-input form-select"
                    value={racePriority}
                    onChange={e => setRacePriority(e.target.value)}
                  >
                    {PRIORITIES.map(p => (
                      <option key={p.value} value={p.value}>{p.label} — {p.desc}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Season</label>
                  <select
                    className="form-input form-select"
                    value={raceSeason}
                    onChange={e => setRaceSeason(e.target.value)}
                  >
                    <option value="">Auto (from date)</option>
                    {SEASON_OPTIONS.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Location (optional)</label>
                  <input
                    className="form-input"
                    type="text"
                    value={raceLocation}
                    onChange={e => setRaceLocation(e.target.value)}
                    placeholder="City, State"
                  />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Notes (optional)</label>
                  <input
                    className="form-input"
                    type="text"
                    value={raceNotes}
                    onChange={e => setRaceNotes(e.target.value)}
                    placeholder="Goals, course notes..."
                  />
                </div>
              </div>
              {error && <p className="form-error">{error}</p>}
              <button className="btn btn-primary" type="submit" disabled={saving} style={{ width: 'auto', padding: '10px 24px', marginTop: 8 }}>
                {saving ? 'Saving...' : (editRaceId ? 'Update Race' : 'Save Race')}
              </button>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-screen" style={{ minHeight: 200 }}>
          <div className="loading-dot-row">
            <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
          </div>
        </div>
      ) : (
        <>
          {upcoming.length === 0 && !showForm && (
            <div className="empty-state">
              <p className="empty-state-title">No upcoming races</p>
              <p className="empty-state-sub">Click "+ Add Race" to build your race calendar.</p>
            </div>
          )}

          {upcoming.length > 0 && (
            <div className="section-card" style={{ marginBottom: 24 }}>
              <div className="section-card-header">
                <span className="section-card-title">Upcoming</span>
              </div>
              <div className="race-list">
                {upcoming.map(race => {
                  const days = daysUntil(race.race_date);
                  return (
                    <div key={race.id} className="race-item">
                      <div className={`race-priority-badge priority-${race.priority}`}>{race.priority}</div>
                      <div className="race-item-main">
                        <div className="race-item-name">{race.name}</div>
                        <div className="race-item-meta">
                          {getRaceEventLabelsWithLeg(race) || null}
                          {race.location && ` · ${race.location}`}
                          {race.notes && ` · ${race.notes}`}
                        </div>
                      </div>
                      <div className="race-item-right">
                        <div className="race-item-date">{formatRaceDateRange(race.race_date, race.race_date_end)}</div>
                        <div className={`race-countdown${days <= 7 ? ' race-countdown--soon' : ''}`}>
                          {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days} days`}
                        </div>
                      </div>
                      <button
                        className="btn-xs btn-xs-ghost"
                        style={{ fontSize: 11 }}
                        onClick={() => {
                          setRaceName(race.name); setRaceDate(race.race_date);
                          setRaceDateEnd(race.race_date_end ?? '');
                          const evts = parseRaceEvents(race.event);
                          setRaceEvents(evts);
                          const legsInit = { ...(race.relay_legs ?? {}) };
                          if (Object.keys(legsInit).length === 0 && race.relay_leg) {
                            const firstRelay = evts.find(isRelayEvent);
                            if (firstRelay) legsInit[firstRelay] = race.relay_leg;
                          }
                          setRelayLegs(legsInit);
                          setEventDates(race.event_dates ?? {});
                          setRaceSeason(race.season ?? '');
                          setRaceLocation(race.location ?? ''); setRacePriority(race.priority);
                          setRaceNotes(race.notes ?? ''); setEditRaceId(race.id); setShowForm(true);
                        }}
                        title="Edit race"
                      >Edit</button>
                      {renderResultInputs(race, days)}
                      <button
                        onClick={() => handleDelete(race.id)}
                        style={{ background: 'none', border: 'none', color: 'var(--gray-300)', cursor: 'pointer', fontSize: 18, lineHeight: 1, paddingLeft: 8 }}
                        title="Delete"
                      >×</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {past.length > 0 && (
            <div className="section-card">
              <div className="section-card-header">
                <span className="section-card-title">Past Races</span>
              </div>
              <div className="race-list">
                {[...past].reverse().map(race => (
                  <div key={race.id} className="race-item race-item--past" style={{ flexWrap: 'wrap' }}>
                    <div className={`race-priority-badge priority-${race.priority}`}>{race.priority}</div>
                    <div className="race-item-main">
                      <div className="race-item-name">{race.name}</div>
                      <div className="race-item-meta">
                        {getRaceEventLabelsWithLeg(race) || null}
                        {race.location && ` · ${race.location}`}
                      </div>
                    </div>
                    <div className="race-item-right">
                      <div className="race-item-date">{formatRaceDate(race.race_date)}</div>
                    </div>
                    <button
                      className="btn-xs btn-xs-ghost"
                      style={{ fontSize: 11 }}
                      onClick={() => {
                        setRaceName(race.name); setRaceDate(race.race_date);
                        setRaceEvents(parseRaceEvents(race.event));
                        setRaceLocation(race.location ?? ''); setRacePriority(race.priority);
                        setRaceNotes(race.notes ?? ''); setEditRaceId(race.id); setShowForm(true);
                      }}
                      title="Edit race"
                    >Edit</button>
                    {renderResultInputs(race)}
                    <button
                      onClick={() => handleDelete(race.id)}
                      style={{ background: 'none', border: 'none', color: 'var(--gray-300)', cursor: 'pointer', fontSize: 18, lineHeight: 1, paddingLeft: 8 }}
                      title="Delete"
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Layout>
  );
}
